import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { SQL } from '@/client/query'
import type { PlatformBannerId } from '@/client/services/banners'
import { PlatformDevice } from '@/client/services/platform/constants'
import { COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG } from '@/client/services/programs/constants'
import { getProgramIterationOpenForRegistration } from '@/client/services/programs/getProgramIterationHelpers'
import composeRichText from '@/client/services/richText/composeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'
import * as url from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import { COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG } from '@/client/services/worlds/constants'
import getUserLevelInWorld from '@/client/services/worlds/getUserLevelInWorld'
import isLevelAtLeastMinLevel from '@/client/services/worlds/isLevelAtLeastMinLevel'

import { makeBannerDataLoader } from './helpers'

const TEMPORARILY_DISABLE_BANNER = true
export default makeBannerDataLoader({
  name: 'caw-workshop',
  load(context) {
    if (TEMPORARILY_DISABLE_BANNER) {
      return null
    }

    if (!hasUserRole(context.user, 'user:approved')) {
      return null
    }

    // In the future, replace this with ProgramIterationQ.getProgramIterationOpenForRegistration. Ideally, we'd use the hook useProgramIterationOpenForRegistration, but we cannot use a hook since this is not a component.
    const cawOpenIterationForTutors = getProgramIterationOpenForRegistration(
      COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG,
      'tutor'
    )

    if (cawOpenIterationForTutors == null || cawOpenIterationForTutors.offerings.length === 0) {
      return null
    }

    const worldMember = context.query.one(
      WorldMemberQ.where({
        userId: context.user.id,
        include: [
          {
            association: 'World',
            required: true,
            where: SQL`${WorldQ.slug} = ${COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG}`,
          },
        ],
      })
    )

    const hasHelpIntent = worldMember(WorldMemberQ.hasHelpIntent('give-help')) ?? false

    const hostingNextCAW =
      context.query<boolean>`EXISTS(SELECT 1 ${SeriesHostQ.where({
        userId: context.user.id,
        include: [
          {
            association: 'Series',
            required: true,
            include: [
              {
                association: 'SeriesTemplate',
                required: true,
                where: SQL`${SeriesTemplateQ.slug} = ANY(${cawOpenIterationForTutors.offerings.map(
                  offering => offering.seriesTemplateSlug
                )})`,
              },
            ],
          },
        ],
      })})` ?? false

    if (!hasHelpIntent || hostingNextCAW || context.user.country !== 'US') {
      return null
    }

    const id: PlatformBannerId = `caw-iteration-${cawOpenIterationForTutors.iterationSlug}-tutor-host-nudge`
    const level = getUserLevelInWorld({
      user: context.user,
      worldSlug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
    })
    const link = isLevelAtLeastMinLevel({ level, minLevel: 'tutor' })
      ? url.getTutorOfferingPageUrl({
          type: 'program',
          slug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
        })
      : url.getDashboardProgramsUrl({ programSlug: COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG })

    const publishDeadlineLong =
      cawOpenIterationForTutors.tutorRegistrationDeadline.toFormat('LLLL d')
    const publishDeadlineShort =
      cawOpenIterationForTutors.tutorRegistrationDeadline.toFormat('LLL d')

    const longText: SerializedRichText = composeRichText(ctx =>
      ctx.paragraph(
        `Create your College Admissions Workshop before the deadline on ${publishDeadlineLong}! It only takes 2 minutes. `,
        ctx.linkInternal(link, 'Create My Workshop')
      )
    )
    const shortText: SerializedRichText = composeRichText(ctx =>
      ctx.paragraph(
        ctx.linkInternal(link, 'Create'),
        ` your College Admissions Workshop by ${publishDeadlineShort}!`
      )
    )

    if (context.user.platformDevice === PlatformDevice.DESKTOP) {
      return {
        slug: id,
        sticky: false,
        content: longText,
      }
    }

    return {
      slug: id,
      sticky: false,
      content: shortText,
    }
  },
})
